import {
  ConditionalFormattingState,
  EnhancedScopeData,
  GetCssClassNamesOption,
} from '@ardoq/data-model';
import {
  ArdoqId,
  ConditionalFormattingAttributes,
  FilterAttributes,
  isConditionalFormatting,
} from '@ardoq/api-types';
import {
  getReferenceCssClassNamesWithScopeData,
  getReferenceCssColorsWithScopeData,
  isComponentIncludedInContextByFilter,
} from '@ardoq/filter-interface';
import { componentInterface } from '@ardoq/component-interface';
import { enhancedScopeDataOperations, componentUtils } from '@ardoq/scope-data';
import { vizFilterGray } from '@ardoq/design-tokens';
import { getFillAndStroke } from '@ardoq/color-helpers';
import { referenceInterface } from '@ardoq/reference-interface';

const getComponentDisplayColor = (
  enhancedScopeData: EnhancedScopeData,
  formattingFilters: ConditionalFormattingAttributes[],
  componentId: ArdoqId,
  options: GetCssClassNamesOption
) => {
  const component = enhancedScopeDataOperations.getComponent(
    enhancedScopeData,
    componentId
  );

  if (!component) {
    return {};
  }

  const componentBaseColor = enhancedScopeDataOperations.getComponentColor(
    enhancedScopeData,
    componentId
  );

  const componentFilter = formattingFilters.some(
    filter => filter.affectComponent
  )
    ? isComponentIncludedInContextByFilter(
        component,
        formattingFilters,
        component,
        enhancedScopeData
      )
    : null;

  const resolvedColor = componentFilter?.color ?? componentBaseColor;

  return getFillAndStroke(resolvedColor, options.useAsBackgroundStyle);
};

const getConditionalFormattingState = (
  enhancedScopeData: EnhancedScopeData,
  formattingFilters: ConditionalFormattingAttributes[],
  componentId: ArdoqId
): ConditionalFormattingState => {
  if (!formattingFilters.length) {
    return ConditionalFormattingState.NORMAL;
  }

  const component = enhancedScopeDataOperations.getComponent(
    enhancedScopeData,
    componentId
  );

  if (!component) {
    return ConditionalFormattingState.NORMAL;
  }

  const componentFilter = formattingFilters.some(
    filter => filter.affectComponent
  )
    ? isComponentIncludedInContextByFilter(
        component,
        formattingFilters,
        component,
        enhancedScopeData
      )
    : null;

  const filterColor = componentFilter?.color;
  if (!filterColor) {
    return ConditionalFormattingState.NORMAL;
  }

  return filterColor === vizFilterGray
    ? ConditionalFormattingState.DIM
    : ConditionalFormattingState.FORMATTED;
};

/**
 * This is a temporary solution to make the transition from BB models to scope data easier.
 *
 * The next step is to create an object with functions that have the same signature as the model interface functions,
 * but are bound to the current scope data. This way we can use the scopeData or BB models interchangeably with minimal
 * refactoring. As soon BB models are no more, we shall pass the scope data instead of the interface object and use the
 * utility functions directly.
 *
 */

export type RepresentationInterface = ReturnType<
  typeof getRepresentationInterfaceWithModelInterfaces
> & {
  getConditionalFormattingState?: (
    componentId: ArdoqId
  ) => ConditionalFormattingState;
};

export const getRepresentationInterfaceWithModelInterfaces = () => ({
  getRepresentationData: componentInterface.getRepresentationData,
  getComponentDisplayColor:
    componentInterface.getComponentDisplayColorAsSVGAttributes,
  getComponentCssClassNames: componentInterface.getCssClassNames,
  getReferenceCssClassNames: referenceInterface.getCssClassNames,
  getReferenceCssColors: referenceInterface.getCssColors,
});

export type LabelsContext = {
  enhancedScopeData: EnhancedScopeData;
  formattingFilters: FilterAttributes[];
};

export const getRepresentationInterfaceForEnhancedScopeData = ({
  enhancedScopeData,
  formattingFilters,
}: LabelsContext): RepresentationInterface => {
  const conditionalFormattingAttributes =
    formattingFilters?.filter(isConditionalFormatting) ?? [];
  return {
    getRepresentationData: (componentId: ArdoqId) =>
      enhancedScopeDataOperations.getComponentRepresentationData(
        enhancedScopeData,
        componentId
      ),

    getComponentDisplayColor: (
      componentId: ArdoqId,
      options: GetCssClassNamesOption
    ) =>
      getComponentDisplayColor(
        enhancedScopeData,
        conditionalFormattingAttributes,
        componentId,
        options
      ),
    getComponentCssClassNames: (
      componentId: ArdoqId,
      options?: GetCssClassNamesOption
    ) =>
      componentUtils.getComponentCSSClassNames({
        scopeData: enhancedScopeData,
        componentId,
        options,
      }),
    getReferenceCssClassNames: (referenceId: ArdoqId) =>
      getReferenceCssClassNamesWithScopeData(
        enhancedScopeData,
        conditionalFormattingAttributes,
        referenceId
      ),
    getReferenceCssColors: (
      referenceId: ArdoqId,
      options: GetCssClassNamesOption
    ) =>
      getReferenceCssColorsWithScopeData(
        enhancedScopeData,
        conditionalFormattingAttributes,
        referenceId,
        options
      ),
    getConditionalFormattingState: (componentId: ArdoqId) =>
      getConditionalFormattingState(
        enhancedScopeData,
        conditionalFormattingAttributes,
        componentId
      ),
  };
};
